import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  SignIn,
  Waitlist,
  useAuth,
  useClerk,
  useUser,
} from '@clerk/clerk-react';
import { setAuthTokenGetter } from './api/client.js';
import { setScreenerAuthTokenGetter } from './api/screener-client.js';
import { CurrentUserProvider, type CurrentUserInfo } from './use-current-user.js';

// Auth boundary. Two modes:
//
//   1. PRODUCTION — VITE_CLERK_PUBLISHABLE_KEY is set. Wrap everything
//      in <ClerkProvider>; show the SignIn flow when signed out and
//      gate the workspace UI behind <SignedIn>.
//
//   2. OPEN-DEV — key is missing. Bypass Clerk entirely and render
//      children straight through. Matches the backend's open-dev mode
//      so frontend + backend agree on identity. A small banner makes
//      the unsafe mode visible.
//
// NEVER deploy without the Clerk key.

const publishableKey =
  (import.meta.env?.['VITE_CLERK_PUBLISHABLE_KEY'] as string | undefined) ?? '';

const authPalette = {
  cream: '#faf6ee',
  dark: '#1a140f',
  darkDim: 'rgba(26, 20, 15, 0.65)',
  darkLine: 'rgba(26, 20, 15, 0.14)',
  ivory: '#f5f0e8',
  ivoryDim: 'rgba(245, 240, 232, 0.72)',
  ivoryLine: 'rgba(245, 240, 232, 0.28)',
};

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!publishableKey) {
    return (
      <>
        <DevModeBanner />
        {children}
      </>
    );
  }
  return (
    <ClerkProvider
      publishableKey={publishableKey}
      // Without these three, every URL Clerk generates for itself falls back to
      // displayConfig — i.e. to the HOSTED Account Portal at
      // keen-basilisk-86.accounts.dev. That portal cannot be themed from here
      // (appearance and localization affect Clerk COMPONENTS only, never
      // Account Portal pages) and it is headed with the Clerk Dashboard's
      // application name, so anyone who reached it saw an unbranded purple
      // card reading "Sign in to healthcare".
      //
      // Pointing them at '/' means Clerk.buildSignInUrl() and friends resolve
      // to this app, where <SignIn> is mounted and our appearance applies.
      signInUrl="/"
      signUpUrl="/"
      waitlistUrl="/"
      localization={{
        signIn: {
          // First line only. The second line — "differently" — is rendered as a
          // block ::after in authCss so it can be set in true italic, which a
          // single text node cannot express. Keeping the break as two separate
          // pieces also means it stays OUR decision at every width rather than
          // whatever the card happens to wrap to.
          start: {
            title: 'See the healthcare market',
            subtitle: '',
            // NOTE: the waitlist action Clerk renders in this footer is
            // hidden outright in authCss — the Sign up tab above the card
            // already leads to the waitlist, so the link is a second door to
            // the same room. Its copy is deliberately NOT overridden here: if
            // that hide rule ever stops matching (as the .cl-headerIcon rule
            // silently did), it should fall back to Clerk's own honest "Want
            // early access? / Join waitlist" rather than to a "Restore
            // credentials" label that would promise password recovery and
            // deliver a waitlist form. Its href is now the waitlistUrl set on
            // ClerkProvider — this app, not Clerk's hosted page — so a broken
            // hide rule degrades to a harmless reload, not a purple detour.
          },
        },
        // The "Sign up" tab mounts <Waitlist>, not <SignUp>.
        //
        // Sign-up mode on this instance is `waitlist`, so <SignUp> only ever
        // rendered a restricted-access card whose one button was a LINK to
        // Clerk's HOSTED /waitlist page — an unbranded purple screen, headed
        // with the Clerk application name, that then linked on to an equally
        // unbranded hosted sign-in ("Sign-ups are currently unavailable").
        // Neither can be themed from here: the hosted Account Portal takes its
        // appearance and its application name from the Clerk Dashboard, not
        // from this appearance object. Mounting the waitlist form ourselves
        // keeps the entire gate inside our own card — and one click shorter.
        waitlist: {
          start: {
            title: 'By invitation only',
            subtitle: '',
            // "Join waitlist" overpromises a queue we don't run. "Request
            // access" describes what actually happens: you ask, we decide.
            // The form still posts to Clerk's waitlist endpoint, which is the
            // right mechanism — it captures the email for us to action.
            formButton: 'Request access',
          },
          success: {
            title: 'Request received',
            subtitle: '',
            message: 'We read every request. You will hear from us by email.',
          },
        },
      }}
    >
      <TokenBridge />
      <SignedIn>
        <CurrentUserBridge>{children}</CurrentUserBridge>
      </SignedIn>
      <SignedOut>
        <SignInScreen />
      </SignedOut>
    </ClerkProvider>
  );
}

// Hands BOTH API clients a token getter that resolves to the latest Clerk
// session JWT. Re-mounts whenever Clerk's loaded state changes. The screener
// client (screener, company overview) has its own getter; it was never wired,
// which went unnoticed until the API started refusing anonymous reads
// (2026-09-16).
function TokenBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setAuthTokenGetter(() => getToken());
    setScreenerAuthTokenGetter(() => getToken());
    return () => {
      setAuthTokenGetter(null);
      setScreenerAuthTokenGetter(null);
    };
  }, [getToken]);
  return null;
}

// Bridges Clerk's useUser/useClerk into the CurrentUser façade so
// the rest of the app reads identity through a single hook.
function CurrentUserBridge({ children }: { children: ReactNode }) {
  const clerk = useClerk();
  const { user } = useUser();
  const value = useMemo<CurrentUserInfo>(
    () => ({
      displayName: user?.fullName ?? user?.firstName ?? user?.username ?? 'User',
      email: user?.primaryEmailAddress?.emailAddress,
      signOut: () => {
        void clerk.signOut();
      },
    }),
    [user, clerk],
  );
  return <CurrentUserProvider value={value}>{children}</CurrentUserProvider>;
}

function SignInScreen() {
  // Two-tab gate: sign-in vs request-access. Clerk doesn't ship a unified
  // component so we toggle locally.
  //
  // `routing="hash"`, NOT "virtual". Virtual routing gives <SignIn> no URL of
  // its own, and clerk-js needs one to bring an OAuth user back: with virtual
  // it built the return leg out of displayConfig instead, so every Google and
  // Microsoft sign-in round-tripped through the HOSTED Account Portal —
  // ...accounts.dev/sign-in#/sso-callback. An existing user only flashed past
  // it; a Google account with no Clerk user got stranded on it, because
  // sign-up mode is `waitlist` and the transfer to sign-up is refused. That
  // stranding screen was unbranded, purple, and headed "Sign in to healthcare".
  //
  // Hash routing keeps the whole flow on our own origin (#/sso-callback,
  // #/factor-one, …) and needs no SPA router and no server-side rewrite — the
  // reason virtual was picked in the first place. Verified by intercepting
  // signIn.authenticateWithRedirect: redirectUrl is now same-origin.
  const [mode, setMode] = useState<'in' | 'up'>('in');
  return (
    <div
      data-auth-mode={mode}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        height: '100vh',
        background:
          'radial-gradient(circle at 16% 12%, rgba(245, 240, 232, 0.1) 0, transparent 26%), #1a140f',
        color: authPalette.ivory,
        padding: 'var(--s-6)',
      }}
    >
      <style>{authCss}</style>
      {/* Deliberate, uneven vertical rhythm. A single uniform `gap` spaced the
          wordmark, the tabs and the card equally, which read as three unrelated
          objects and left the tabs floating. The tabs SWITCH the card, so they
          belong to it: large space under the wordmark, small space above the
          card. Proximity does the grouping. */}
      <div
        aria-label="Corryk"
        style={{
          fontFamily: 'Fraunces, var(--font-editorial)',
          fontSize: 36,
          fontWeight: 500,
          letterSpacing: '-0.02em',
          lineHeight: 1,
          marginBottom: 'var(--s-6)',
        }}
      >
        Corryk
      </div>
      <div
        role="tablist"
        style={{
          display: 'inline-flex',
          gap: 0,
          border: `1px solid ${authPalette.ivoryLine}`,
          borderRadius: 2,
          background: 'rgba(245, 240, 232, 0.06)',
          fontSize: 'var(--t-body)',
          marginBottom: 'var(--s-3)',
          overflow: 'hidden',
        }}
      >
        <TabBtn active={mode === 'in'} onClick={() => setMode('in')}>
          Sign in
        </TabBtn>
        <TabBtn active={mode === 'up'} onClick={() => setMode('up')}>
          Sign up
        </TabBtn>
      </div>
      {mode === 'up' ? (
        <Waitlist appearance={authAppearance} />
      ) : (
        <SignIn routing="hash" forceRedirectUrl="/" appearance={authAppearance} />
      )}
    </div>
  );
}

// The brand wordmark face, matching the workspace top bar exactly
// (top-bar.tsx). Fraunces is loaded at weight 500 ONLY — asking for 400 gets a
// synthesised face, which is why the headline is pinned to 500 below.
const BRAND_SERIF = '"Fraunces", "Source Serif 4", Georgia, serif';
const BRAND_UI = 'Inter, var(--font-ui)';

const authAppearance = {
  layout: {
    // Clerk stamps a "Development mode" badge into the card footer, under
    // "Secured by Clerk", whenever the publishable key is a pk_test_ one.
    //
    // This hides the badge. It does NOT make the instance a production one:
    // the key in use resolves to keen-basilisk-86.clerk.accounts.dev, still a
    // DEVELOPMENT instance, with development limits and development security.
    // The real fix is a pk_live_ key from a production Clerk instance; this
    // only stops the front door announcing the fact.
    unsafe_disableDevelopmentModeWarnings: true,
  },
  variables: {
    borderRadius: '2px',
    colorBackground: authPalette.cream,
    colorDanger: '#d1242f',
    colorInputBackground: authPalette.ivory,
    colorInputText: authPalette.dark,
    colorPrimary: authPalette.dark,
    colorText: authPalette.dark,
    colorTextOnPrimaryBackground: authPalette.ivory,
    colorTextSecondary: authPalette.darkDim,
    fontFamily: BRAND_UI,
    fontFamilyButtons: BRAND_UI,
    // MUST be an absolute unit. Clerk derives its entire type scale from this
    // in `rem`, and this app pins the root font-size to 11px for workspace
    // density — so Clerk's 0.8125rem default was computing to 8.94px and every
    // label, button and footer in the form rendered at ~9px. The sign-in screen
    // is the front door, not the dense grid: 14px is the comfortable reading
    // size the rest of the scale now hangs off.
    fontSize: '14px',
  },
  elements: {
    rootBox: {
      width: 'min(100%, 420px)',
    },
    cardBox: {
      background: authPalette.cream,
      border: `1px solid ${authPalette.ivoryLine}`,
      borderRadius: '2px',
      boxShadow: '0 22px 70px rgba(0, 0, 0, 0.28)',
      overflow: 'hidden',
      width: '100%',
    },
    card: {
      background: authPalette.cream,
      borderRadius: '2px',
      color: authPalette.dark,
      padding: '32px',
    },
    header: {
      gap: 0,
      // Air under a headline that is now two lines — 10px let the social
      // buttons crowd the descenders.
      paddingBottom: '22px',
    },
    headerTitle: {
      color: authPalette.dark,
      fontFamily: BRAND_SERIF,
      fontSize: '25px',
      // 500 = the only Fraunces weight actually loaded, and the weight the
      // workspace wordmark uses. 400 was being synthesised by the browser.
      fontWeight: 500,
      letterSpacing: '-0.015em',
      lineHeight: 1.18,
      textAlign: 'center',
      // .cl-header is a flex column; without a full-width box the h1 shrinks to
      // its content and text-align has nothing to centre within.
      width: '100%',
      // 'pre-line', not 'nowrap': honours the explicit \n in the sign-in title
      // while still collapsing incidental whitespace. 'nowrap' forced the
      // headline onto a single line and would have suppressed the break.
      whiteSpace: 'pre-line',
    },
    headerSubtitle: {
      display: 'none',
    },
    socialButtonsBlockButton: {
      background: authPalette.ivory,
      border: `1px solid ${authPalette.darkLine}`,
      color: authPalette.dark,
      fontFamily: BRAND_UI,
      fontSize: '13px',
      fontWeight: 500,
      height: '38px',
    },
    formFieldLabel: {
      color: authPalette.dark,
      fontFamily: BRAND_UI,
      fontSize: '12px',
      fontWeight: 500,
    },
    formFieldInput: {
      background: authPalette.ivory,
      border: `1px solid ${authPalette.darkLine}`,
      color: authPalette.dark,
      borderRadius: '2px',
      fontFamily: BRAND_UI,
      fontSize: '14px',
      height: '38px',
    },
    formButtonPrimary: {
      background: authPalette.dark,
      borderRadius: '2px',
      color: authPalette.ivory,
      fontFamily: BRAND_UI,
      fontSize: '13px',
      fontWeight: 600,
      height: '38px',
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
    footer: {
      fontFamily: BRAND_UI,
      fontSize: '12px',
    },
    footerActionText: {
      fontFamily: BRAND_UI,
      fontSize: '12px',
    },
    footerActionLink: {
      color: authPalette.dark,
      fontFamily: BRAND_UI,
      fontSize: '12px',
      fontWeight: 600,
    },
    dividerLine: {
      background: authPalette.darkLine,
    },
    dividerText: {
      color: authPalette.darkDim,
      fontFamily: BRAND_UI,
      fontSize: '12px',
    },
  },
} as const;

const authCss = `
  /* Hide whatever header glyph the up-mode card ships with — Clerk's stock
     one is a generic "prohibited" circle-slash, which reads as an error state
     rather than an invitation.

     This previously targeted .cl-headerIcon — a class this Clerk version no
     longer emits, so the rule was dead and the glyph had been showing. The
     icon now lives in an internal-classed wrapper inside .cl-header, so the
     header region is the stable hook. Scoped to the header on purpose: a bare
     '.cl-card svg' would also strip the Google/Microsoft logos. */
  [data-auth-mode='up'] .cl-header svg {
    display: none !important;
  }

  /* The up-mode title comes from Clerk localization (waitlist.start.title) and
     is styled by headerTitle in authAppearance, exactly like the sign-in one.
     The old font-size:0 + ::after duplicate of that string is gone: it was
     redundant with the localization, and it silently pinned its own font and
     size, so restyling the headline left the card behind. */

  [data-auth-mode='up'] .cl-headerSubtitle {
    display: none !important;
  }

  /* The waitlist card's own "Already have access? / Sign in" footer action.

     The signInUrl on ClerkProvider already keeps its href on our own origin —
     without that it pointed at Clerk's HOSTED sign-in, the unbranded screen
     headed with the Clerk application name. It is hidden anyway because the
     tab row directly above the card does exactly this job, and two controls
     one line apart that both mean "go to sign-in" is one too many.

     Mode-scoped, and in up-mode the waitlist card is the only card on screen,
     so a plain .cl-footerAction is a stable hook that needs no generated
     cl-internal-* class. The sign-in card keeps its own, narrower rule below. */
  [data-auth-mode='up'] .cl-footerAction {
    display: none;
  }

  /* The app's global form reset is
       input[type="text"], input[type="email"], … { font-size: var(--t-body) }
     — an attribute+element selector (0,1,1) that OUTRANKS the single generated
     class Clerk puts on the field (0,1,0). So the appearance object's 14px lost
     and the email field rendered at the workspace's 11px, out of step with the
     label and button beside it. Buttons were unaffected: their reset is a bare
     element selector (0,0,1), which the class beats.

     Two classes + the element gets us to 0,2,1 without !important. The same
     reset also imposes the workspace's tight 4px/8px padding, which squeezed
     the field against the 38px buttons either side of it.

     max-height is the rem trap again, in a property the appearance API does
     not expose: Clerk emits height:38px together with max-height:2.25rem on
     the field, and 2.25rem against this app's 11px root is 24.75px — so
     Clerk's own max-height was overriding Clerk's own height. Releasing the
     clamp is what actually lets the field match the buttons. */
  .cl-card input.cl-formFieldInput {
    font-size: 14px;
    padding: 9px 10px;
    height: 38px;
    max-height: none;
  }

  /* Remove the waitlist action from the SIGN-IN footer.

     We don't run a waitlist, so "Want early access? / Join waitlist" promises
     something that doesn't exist — and the link's href is Clerk's hosted
     /waitlist page, generated by Clerk and not repointable through
     localization. Relabelling it alone would have been worse than leaving it:
     the text would offer password recovery and the click would deliver a
     waitlist form.

     cl-footerAction__signIn is Clerk's own semantic modifier, not one of its
     generated cl-internal-* classes, so it is a stable hook. The sign-up card
     carries __signUp instead (verified), which is why "Already have an
     account? Sign in" survives this rule. Scoped to in-mode as well, so the
     two can never collide.

     Password recovery falls to Clerk's native flow at the password step. */
  [data-auth-mode='in'] .cl-footerAction__signIn {
    display: none;
  }

  /* The payoff word, set in true italic on its own line.

     It has to be generated content: the headline arrives from Clerk
     localization as ONE text node, and CSS cannot italicise part of a text
     node. Only content/display/font-style are declared here — family, size,
     weight, colour and the narrow-viewport step-down are all INHERITED from
     .cl-headerTitle. That is deliberately unlike the old sign-up ::after this
     replaced, which redeclared its own font and size and so silently missed
     every later restyle.

     Real italic, not a synthesised slant — the Fraunces ital axis is loaded in
     tokens.css for exactly this. Sign-in only: "By invitation only" is a
     complete sentence and takes no second line.

     SCOPED TO THE START STEP. The sign-in flow is many screens sharing one
     .cl-headerTitle — password, forgot-password, verification codes, MFA. Keyed
     on mode alone this appended "differently" to every one of them, so the
     password screen read "Enter your password / differently". It shipped, and
     was caught in production rather than here. .cl-signIn-start is the
     card-level modifier Clerk puts on the first step only; later steps carry
     their own, so they no longer match. */
  [data-auth-mode='in'] .cl-signIn-start .cl-headerTitle::after {
    content: 'differently';
    display: block;
    font-style: italic;
  }

  /* Narrow viewports. 'pre-line' honours our explicit break but still allows
     normal wrapping, so at 375px the 25px headline spilled to THREE lines and
     orphaned the word "market". Stepping the size down keeps the intended
     two-line break — the break stays a design decision at every width, which
     was the point of setting it explicitly.
     Two classes (0,2,0) to outrank Clerk's generated single class. */
  @media (max-width: 460px) {
    .cl-card .cl-headerTitle {
      font-size: 21px;
    }
  }
`;

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: '7px 20px',
        border: 'none',
        background: active ? authPalette.ivory : 'transparent',
        color: active ? authPalette.dark : authPalette.ivoryDim,
        cursor: 'pointer',
        fontFamily: BRAND_UI,
        // Explicit px, not var(--t-body): that token is 11px, tuned for
        // workspace density, and reads as fine print on the front door.
        fontSize: '13px',
        fontWeight: 600,
        letterSpacing: '0.01em',
        transition: 'background 120ms ease, color 120ms ease',
      }}
    >
      {children}
    </button>
  );
}

function DevModeBanner() {
  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        background: 'var(--color-negative-500)',
        color: '#fff',
        padding: '2px var(--s-3)',
        fontSize: 'var(--t-meta)',
        fontFamily: 'var(--font-mono)',
        textAlign: 'center',
        letterSpacing: '0.04em',
      }}
    >
      OPEN-DEV MODE — VITE_CLERK_PUBLISHABLE_KEY unset · all requests run as dev-user · never deploy
    </div>
  );
}
